import React, { useState } from 'react';
import type { PortfolioItem, Portfolio } from '../types/interfaces';
import PortfolioItemComponent from './PortfolioItem';
import PortfolioAnalytics from './PortfolioAnalytics';
import AdvancedPortfolioAnalytics from './AdvancedPortfolioAnalytics';
import PortfolioManager from './PortfolioManager';
import ExportModal from './ExportModal';
import { ExportFormat } from '../utils/export';

interface PortfolioTabProps {
  portfolio: PortfolioItem[];
  portfolios: Portfolio[];
  currentPortfolioId: string;
  onAddCoin: () => void;
  onRemoveCoin: (index: number) => void;
  onSwitchPortfolio: (portfolioId: string) => void;
  onCreatePortfolio: (name: string) => Promise<string>;
  onDeletePortfolio: (portfolioId: string) => void;
  onRenamePortfolio: (portfolioId: string, newName: string) => void;
  currency?: string;
  analyticsUnlocked?: boolean;
  onUnlockAnalytics?: () => Promise<void>;
  onUnlockAdvancedAnalytics?: () => Promise<boolean>;
  multiplePortfoliosUnlocked?: boolean;
  onUnlockMultiplePortfolios?: () => Promise<boolean>;
}

function formatCurrency(num: number): string {
  if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
  if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
  return `$${num.toFixed(2)}`;
}

export default function PortfolioTab({ 
  portfolio,
  portfolios,
  currentPortfolioId,
  onAddCoin, 
  onRemoveCoin,
  onSwitchPortfolio,
  onCreatePortfolio,
  onDeletePortfolio,
  onRenamePortfolio,
  currency = 'usd',
  analyticsUnlocked = false,
  onUnlockAnalytics,
  onUnlockAdvancedAnalytics,
  multiplePortfoliosUnlocked = false,
  onUnlockMultiplePortfolios
}: PortfolioTabProps) {
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const handleExport = async (format: ExportFormat): Promise<boolean> => {
    if (!window.bsvPayments || !window.bsvPayments.requestPayment) {
      alert('Connect MetaNet wallet');
      return false;
    }

    try {
      const amount = 200; // 200 satoshis per export
      const result = await window.bsvPayments.requestPayment('EXPORT_DATA', amount);
      return !!result;
    } catch (error) {
      console.error('Export payment failed:', error);
      return false;
    }
  };
  const totalValue = portfolio.reduce(
    (sum, item) => sum + (item.quantity || 0) * (item.currentPrice || 0),
    0
  );
  
  const totalCost = portfolio.reduce(
    (sum, item) => sum + (item.quantity || 0) * (item.purchasePrice || item.currentPrice || 0),
    0
  );
  
  const totalProfit = totalValue - totalCost;
  const totalProfitPercent = totalCost > 0 ? (totalProfit / totalCost) * 100 : 0;
  
  const totalChange24h = portfolio.reduce((sum, item) => {
    const value = (item.quantity || 0) * (item.currentPrice || 0);
    const change = (item.change24h || 0) / 100;
    return sum + (value * change);
  }, 0);
  
  const totalChange24hPercent = totalValue > 0 ? (totalChange24h / totalValue) * 100 : 0;

  if (portfolio.length === 0) {
    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl">Your Portfolio</h2>
          <button
            onClick={onAddCoin}
            className="px-6 py-3 bg-success border-none rounded-lg text-white text-base cursor-pointer transition-all hover:bg-green-700 hover:-translate-y-0.5 hover:shadow-lg"
          >
            + Add Coin
          </button>
        </div>
        <div className="text-center py-20 px-5">
          <div className="text-8xl mb-6">💼</div>
          <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r from-text to-primary bg-clip-text text-transparent">
            Your Portfolio is Empty
          </h3>
          <p className="text-text-secondary text-lg mb-8">
            Start tracking your crypto investments by adding coins to your portfolio!
          </p>
          <button
            onClick={onAddCoin}
            className="px-10 py-5 bg-gradient-to-r from-success to-green-400 border-none rounded-full text-white text-lg font-bold cursor-pointer transition-all duration-300 hover:scale-110 hover:shadow-bubble-lg hover:shadow-glow-green active:scale-95"
          >
            ✨ Add Your First Coin
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Portfolio Manager */}
      <PortfolioManager
        portfolios={portfolios}
        currentPortfolioId={currentPortfolioId}
        onSwitchPortfolio={onSwitchPortfolio}
        onCreatePortfolio={onCreatePortfolio}
        onDeletePortfolio={onDeletePortfolio}
        onRenamePortfolio={onRenamePortfolio}
        isUnlocked={multiplePortfoliosUnlocked}
        onUnlock={onUnlockMultiplePortfolios || (async () => false)}
        currency={currency}
      />

      <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
        <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">💼 Your Portfolio</h2>
        <div className="flex gap-3">
          <button
            onClick={() => setExportModalOpen(true)}
            className="px-6 py-4 bg-gradient-to-r from-primary to-primary-light border-none rounded-full text-white text-base font-semibold cursor-pointer transition-all duration-300 hover:scale-110 hover:shadow-bubble-lg active:scale-95"
            title="Export Portfolio (200 sats)"
          >
            📤 Export
          </button>
          <button
            onClick={onAddCoin}
            className="px-8 py-4 bg-gradient-to-r from-success to-green-400 border-none rounded-full text-white text-base font-bold cursor-pointer transition-all duration-300 hover:scale-110 hover:shadow-bubble-lg hover:shadow-glow-green active:scale-95"
          >
            ✨ + Add Coin
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary p-8 rounded-3xl text-center shadow-bubble border-2 border-primary/10 hover:border-primary/30 transition-all duration-300 hover:scale-105 hover:shadow-bubble-lg">
          <div className="text-4xl mb-3">💰</div>
          <span className="block text-text-secondary text-sm font-bold uppercase tracking-wider mb-3">Total Value</span>
          <span className="block text-4xl font-bold bg-gradient-to-r from-text to-primary bg-clip-text text-transparent">{formatCurrency(totalValue)}</span>
        </div>
        <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary p-8 rounded-3xl text-center shadow-bubble border-2 border-primary/10 hover:border-primary/30 transition-all duration-300 hover:scale-105 hover:shadow-bubble-lg">
          <div className="text-4xl mb-3">📈</div>
          <span className="block text-text-secondary text-sm font-bold uppercase tracking-wider mb-3">24h Change</span>
          <span
            className={`block text-4xl font-bold ${
              totalChange24hPercent >= 0 
                ? 'text-green-400' 
                : 'text-red-400'
            }`}
          >
            {totalChange24hPercent >= 0 ? '↗' : '↘'} {totalChange24hPercent >= 0 ? '+' : ''}
            {totalChange24hPercent.toFixed(2)}%
          </span>
        </div>
      </div>

      {/* Portfolio Analytics */}
      <div className="mb-8">
        <PortfolioAnalytics
          portfolio={portfolio}
          currency={currency}
          isUnlocked={analyticsUnlocked}
          onUnlock={onUnlockAnalytics || (async () => {})}
        />
      </div>

      {/* Advanced Portfolio Analytics */}
      <div className="mb-8">
        <AdvancedPortfolioAnalytics
          portfolio={portfolio}
          currency={currency}
          onUnlock={onUnlockAdvancedAnalytics || (async () => false)}
        />
      </div>
      
      <div className="flex flex-col gap-3">
        {portfolio.map((item, index) => (
          <PortfolioItemComponent
            key={`${item.id}-${index}`}
            item={item}
            index={index}
            onRemove={onRemoveCoin}
          />
        ))}
      </div>

      <ExportModal
        isOpen={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        portfolio={portfolio}
        currency={currency}
        onExport={handleExport}
      />
    </div>
  );
}

